import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { prayerMethodConfig } from "@/lib/constants";
import { defaultLocationId, prayerLocations } from "@/lib/locations";
import { logger } from "@/lib/logger";
import { buildPrayerCatalog, getNextCatalogRefreshAt } from "@/lib/prayer-calculator";
import { getPrayerMethodConfig } from "@/lib/prayer-methods";
import type { PrayerMethodConfig, PrayerMethodValue, PrayerSnapshot } from "@/types/prayer";

type PrayerStoreState = {
  _timeoutId: ReturnType<typeof setTimeout> | null;
  isReady: boolean;
  methodConfig: PrayerMethodConfig;
  snapshots: Record<string, PrayerSnapshot>;
};

type PrayerStoreActions = {
  initialize: () => () => void;
  refreshSnapshots: () => void;
  scheduleRefresh: () => void;
  setMethod: (method: PrayerMethodValue) => void;
};

type PrayerStore = PrayerStoreState & PrayerStoreActions;

const clearTimer = (timeoutId: ReturnType<typeof setTimeout> | null) => {
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
};

export const usePrayerStore = create<PrayerStore>()(
  subscribeWithSelector((set, get) => ({
    _timeoutId: null,
    initialize: () => {
      get().refreshSnapshots();
      get().scheduleRefresh();

      return () => {
        clearTimer(get()._timeoutId);
        set({ _timeoutId: null });
      };
    },
    isReady: false,
    methodConfig: prayerMethodConfig,
    refreshSnapshots: () => {
      try {
        const now = new Date();
        const activeMethod = get().methodConfig;
        const snapshots = buildPrayerCatalog(prayerLocations, now, activeMethod);
        const current = snapshots[defaultLocationId] ?? Object.values(snapshots)[0];

        set({
          isReady: current !== undefined,
          snapshots,
        });

        logger.info("Prayer catalog refreshed", {
          locationCount: Object.keys(snapshots).length,
          method: activeMethod.method,
          ready: current !== undefined,
        });
      } catch (error) {
        logger.captureError("Prayer catalog refresh failed", error);
        set({
          isReady: false,
          snapshots: {},
        });
      }
    },
    scheduleRefresh: () => {
      const state = get();
      clearTimer(state._timeoutId);

      if (Object.keys(state.snapshots).length === 0) {
        logger.warn("Prayer catalog refresh skipped because no snapshots are available yet.");
        return;
      }

      const now = new Date();
      const nextRefresh = getNextCatalogRefreshAt(prayerLocations, state.snapshots, now);
      const delay = Math.max(1000, nextRefresh - now.getTime());
      const timeoutId = setTimeout(() => {
        get().refreshSnapshots();
        get().scheduleRefresh();
      }, delay);

      logger.debug("Scheduled next prayer catalog refresh", {
        delayMs: delay,
        nextRefreshIso: new Date(nextRefresh).toISOString(),
      });

      set({ _timeoutId: timeoutId });
    },
    setMethod: (method) => {
      const nextMethodConfig = getPrayerMethodConfig(method);
      const currentMethodConfig = get().methodConfig;

      if (
        currentMethodConfig.method === nextMethodConfig.method &&
        currentMethodConfig.fajrAngle === nextMethodConfig.fajrAngle &&
        currentMethodConfig.ishaAngle === nextMethodConfig.ishaAngle &&
        currentMethodConfig.ishaInterval === nextMethodConfig.ishaInterval
      ) {
        return;
      }

      set({ methodConfig: nextMethodConfig });
      logger.info("Prayer calculation method changed", {
        method: nextMethodConfig.method,
      });
      get().refreshSnapshots();
      get().scheduleRefresh();
    },
    snapshots: {},
  })),
);
